/** QuickRouter — Vue 3D de la carte assemblée (React Three Fiber) : époxy percé au vernis
 *  de la couleur choisie, pistes et pastilles, volumes des composants (dessus et dessous).
 *  Repère : X, Y de la carte, Z vers le haut (la face dessus est à Z = épaisseur). */
import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { useProjectStore } from '../store/useProjectStore.js';
import { worldPads, placePoint, polyBox, polyArea } from '../../shared/geom.js';

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

const MASK = { vert: '#11622f', bleu: '#174d93', rouge: '#8c1b1b', noir: '#1a1a1a', blanc: '#ecece6', jaune: '#c9a21a', violet: '#5a2784' };
const COPPER = '#d8b25a';
const CU = 0.035;

function boardGeometry(project, holes) {
  const pts = polyArea(project.board.outline) < 0 ? [...project.board.outline].reverse() : project.board.outline;
  const shape = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
  for (const h of holes) {
    const path = new THREE.Path();
    path.absarc(h.pos[0], h.pos[1], h.r, 0, Math.PI * 2, true);
    shape.holes.push(path);
  }
  return new THREE.ExtrudeGeometry(shape, { depth: project.board.thickness, bevelEnabled: false, curveSegments: 16 });
}

function Segment({ a, b, w, z, color }) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  return (
    <group position={[(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, z]} rotation={[0, 0, Math.atan2(dy, dx)]}>
      <mesh><boxGeometry args={[len, w, CU]} /><meshStandardMaterial color={color} metalness={0.6} roughness={0.35} /></mesh>
      <mesh position={[len / 2, 0, 0]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[w / 2, w / 2, CU, 12]} /><meshStandardMaterial color={color} metalness={0.6} roughness={0.35} /></mesh>
      <mesh position={[-len / 2, 0, 0]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[w / 2, w / 2, CU, 12]} /><meshStandardMaterial color={color} metalness={0.6} roughness={0.35} /></mesh>
    </group>
  );
}

function Pad({ p, z }) {
  const [w, h] = p.size;
  const round = p.shape === 'circle';
  return (
    <group position={[p.pos[0], p.pos[1], z]} rotation={[0, 0, (p.rot * Math.PI) / 180]}>
      {round
        ? <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[Math.max(w, h) / 2, Math.max(w, h) / 2, CU * 1.5, 20]} /><meshStandardMaterial color="#e6e3dc" metalness={0.8} roughness={0.3} /></mesh>
        : <mesh><boxGeometry args={[w, h, CU * 1.5]} /><meshStandardMaterial color="#e6e3dc" metalness={0.8} roughness={0.3} /></mesh>}
    </group>
  );
}

function Body({ comp, fp, t }) {
  const b = fp.body;
  if (!b) return null;
  const [w, h, ht] = b.size;
  const c = placePoint(comp, b.offset ?? [0, 0]);
  const bottom = comp.side === 'bottom';
  return (
    <group position={[c[0], c[1], bottom ? -ht / 2 : t + ht / 2]} rotation={[0, 0, ((comp.rot || 0) * Math.PI) / 180]}>
      <mesh castShadow><boxGeometry args={[w, h, ht]} /><meshStandardMaterial color={b.color} roughness={0.55} metalness={fp.category === 'Modules' ? 0.7 : 0.1} /></mesh>
    </group>
  );
}

/** Carte mère en fantôme sous la carte fille : son circuit et ses obstacles en volume. */
function Mother({ mother, t }) {
  const geo = useMemo(() => {
    const pts = polyArea(mother.outline) < 0 ? [...mother.outline].reverse() : mother.outline;
    return new THREE.ExtrudeGeometry(new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y))), { depth: mother.thickness, bevelEnabled: false });
  }, [mother]);
  // Face de la mère tournée vers la carte : son dessus (shield) ou son dessous (porteuse).
  const above = mother.side === 'above';
  const face = above ? t + mother.gap : -mother.gap;
  const dir = above ? -1 : 1;
  return (
    <group>
      <mesh geometry={geo} position={[0, 0, above ? face : face - mother.thickness]}>
        <meshStandardMaterial color="#1f4c8f" transparent opacity={0.45} roughness={0.6} />
      </mesh>
      {mother.obstacles.map((o, i) => (o.kind === 'circle'
        ? (
          <mesh key={i} position={[o.c[0], o.c[1], face + (dir * o.height) / 2]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[o.r, o.r, o.height, 20]} /><meshStandardMaterial color="#8d96a3" transparent opacity={0.55} />
          </mesh>
        ) : (
          <mesh key={i} position={[(o.min[0] + o.max[0]) / 2, (o.min[1] + o.max[1]) / 2, face + (dir * o.height) / 2]}>
            <boxGeometry args={[o.max[0] - o.min[0], o.max[1] - o.min[1], o.height]} /><meshStandardMaterial color="#8d96a3" transparent opacity={0.4} />
          </mesh>
        )))}
    </group>
  );
}

export default function Board3D() {
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore.getState().lookup();
  const t = project.board.thickness;

  const data = useMemo(() => {
    const pads = project.components.flatMap((c) => worldPads(c, lookup(c.footprint)));
    const holes = [
      ...pads.filter((p) => p.drill).map((p) => ({ pos: p.pos, r: p.drill / 2 })),
      ...project.vias.map((v) => ({ pos: v.pos, r: v.drill / 2 })),
    ];
    return { pads, geo: boardGeometry(project, holes) };
  }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

  const [x0, y0, x1, y1] = polyBox(project.board.outline);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, span = Math.max(x1 - x0, y1 - y0);

  return (
    <Canvas shadows camera={{ position: [cx + span * 0.3, cy - span * 1.6, span * 1.3], fov: 40, near: 0.5, far: span * 20 }} style={{ background: 'linear-gradient(#2a2d33, #15161a)' }}>
      <ambientLight intensity={0.55} />
      <directionalLight position={[cx + span, cy - span, span * 2]} intensity={1.6} castShadow />
      <directionalLight position={[cx - span, cy + span, -span]} intensity={0.5} />
      {project.board.mother && <Mother mother={project.board.mother} t={t} />}
      <mesh geometry={data.geo}><meshStandardMaterial color={MASK[project.board.color] ?? MASK.vert} roughness={0.5} metalness={0.05} /></mesh>
      {/* Couches internes non dessinées : elles sont dans l'époxy (opaque). */}
      {project.tracks.filter((tr) => tr.layer === 'F' || tr.layer === 'B').map((tr) => tr.pts.slice(1).map((b, i) => (
        <Segment key={`${tr.id}-${i}`} a={tr.pts[i]} b={b} w={tr.width} z={tr.layer === 'F' ? t + CU / 2 : -CU / 2} color={tr.layer === 'F' ? '#3fae62' : '#2f8a4b'} />
      )))}
      {data.pads.map((p) => (
        p.layers.includes('F') || p.layers.includes('B') ? (
          <group key={`${p.key}-${p.idx}`}>
            {p.layers.includes('F') && <Pad p={p} z={t + CU / 2} />}
            {p.layers.includes('B') && <Pad p={p} z={-CU / 2} />}
          </group>
        ) : null
      ))}
      {project.vias.map((v) => (
        <group key={v.id}>
          <mesh position={[v.pos[0], v.pos[1], t / 2]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[v.diameter / 2, v.diameter / 2, t + CU * 2, 16, 1, true]} /><meshStandardMaterial color={COPPER} metalness={0.7} roughness={0.3} side={THREE.DoubleSide} /></mesh>
        </group>
      ))}
      {project.components.map((c) => {
        const fp = lookup(c.footprint);
        return fp ? <Body key={c.id} comp={c} fp={fp} t={t} /> : null;
      })}
      <OrbitControls target={[cx, cy, t]} makeDefault />
    </Canvas>
  );
}
